(function () {
  'use strict';

  // Load marker, so it is possible to confirm from the console that this ran.
  window.__jdgmA11y = true;

  /* =====================================================================
     Cancelling a form navigates the page, and never restores focus
     (WCAG 3.2.2 On Input, 2.4.3 Focus Order)

     Judge.me renders both cancel controls as anchors pointing at the reviews
     section:
       <a href="#judgeme_product_reviews" role="button" class="jdgm-btn jdgm-cancel-question">

     Two problems, one shared and one not:

     1. Their review-form handler calls preventDefault before closing; the
        question-form handler does not, so cancelling a question closes the form
        and then follows the href, navigating the page:
          .jdgm-cancel-rev      -> function (e) { e.preventDefault(); jdgm.closeForm(...) }
          .jdgm-cancel-question -> function (e) { jdgm.closeQuestionForm(...) }

     2. Neither restores focus. Dismissing the form leaves focus on a control that
        no longer exists on screen, so keyboard and screen reader users are dropped
        at the top of the document instead of back where they started.

     Both are handled the same way below. Focus is deferred a tick because their
     close handler relabels and re-shows the trigger (jdgm.toggleWriteReviewLink) -
     focusing it any earlier can land on it mid-state.

     Every listener here runs in the CAPTURE phase. Judge.me's handlers are
     jQuery ones that `return false`, and jQuery translates that into
     preventDefault + stopPropagation - so a normal bubble-phase listener on
     document never runs at all. Capture fires before the target's own handlers,
     where stopPropagation cannot reach it. preventDefault still works from here,
     since the default action is only evaluated once propagation finishes.
     ===================================================================== */
  var CANCEL_TRIGGERS = {
    '.jdgm-cancel-rev': '.jdgm-write-rev-link',
    '.jdgm-cancel-question': '.jdgm-ask-question-btn'
  };

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    Object.keys(CANCEL_TRIGGERS).forEach(function (cancelSelector) {
      if (!e.target.closest(cancelSelector)) return;

      // Redundant for the review form, whose handler already prevents it, but it
      // keeps the two paths identical and costs nothing.
      e.preventDefault();

      var triggerSelector = CANCEL_TRIGGERS[cancelSelector];
      window.setTimeout(function () {
        var trigger = document.querySelector(triggerSelector);
        if (trigger) trigger.focus();
      }, 0);
    });
  }, true);

  /* =====================================================================
     Review badge scrolls to the reviews without moving focus
     (WCAG 2.4.3 Focus Order)

     The star rating beside the price is Judge.me's preview badge:
       <span class="jdgm-prev-badge__stars" role="button" tabindex="0" aria-label="5.00 stars">

     Clicking it runs jdgm._openTabAndScrollToRevWidget(), which animates the
     viewport down to the review widget but leaves focus on the badge. Tabbing
     then continues from the top of the page while the reader is looking at the
     reviews.

     Move focus to the reviews heading instead. preventScroll keeps us from
     fighting Judge.me's own scroll animation - it delivers the viewport, we
     just move the focus ring to match.
     ===================================================================== */
  function focusReviewsHeading() {
    var heading = document.querySelector('.jdgm-rev-widg__title');
    if (!heading) return;

    // Headings are not focusable on their own. -1 makes it programmatically
    // focusable without adding it to the tab order.
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest('.jdgm-prev-badge')) return;
    focusReviewsHeading();
  }, true);

  /* =====================================================================
     The badge is marked role="button" tabindex="0", but Judge.me only binds a
     click handler - it registers no keydown anywhere - so Enter and Space do
     nothing. Native buttons fire click on both keys; a span with role="button"
     does not, so the activation has to be synthesised.

     Without this, the focus fix above is unreachable by keyboard: you can tab
     to the badge but never activate it.
     ===================================================================== */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;

    var badge = e.target.closest('.jdgm-prev-badge');
    if (!badge || !e.target.closest('[role="button"]')) return;

    // Space would otherwise scroll the page.
    e.preventDefault();

    // Runs Judge.me's own click handler, which scrolls; our click listener above
    // then moves focus to the heading.
    badge.click();
  }, true);
  /* =====================================================================
     Star rating in the Write a Review form is not keyboard operable
     (WCAG 2.1.1 Keyboard)

     Judge.me builds the rating with jQuery Raty, which renders anchors with no
     href and binds mouse events only:
       <span class="jdgm-form__rating" role="radiogroup">
         <a class="jdgm-star jdgm--off" role="radio" aria-checked="false" tabindex="-1">
         ... x5
         <input name="score" type="hidden">

     The roles are present, but EVERY star sits at tabindex="-1" and the group
     itself has none, so nothing in the control is in the tab order - Tab skips
     from the form straight to Review Title.

     Judge.me DOES handle the keys and the roving tabindex - jdgm._setupFillInStarsShortCuts
     binds Left/Right and 1-5, and their score setter rewrites tabindex and
     aria-checked. But that only runs once a score has been set: on first render
     every star is left at tabindex="-1", so there is no tab stop to arrive at
     and the control is unreachable.

     So all that is needed is the initial roving tabindex - one star reachable by
     Tab before anything is chosen. Do NOT add key handling here: Judge.me's
     handler is already bound, and a second one double-steps the rating.
     ===================================================================== */
  function getStars(group) {
    return Array.prototype.slice.call(group.querySelectorAll('.jdgm-star'));
  }

  function getScore(group) {
    var input = group.querySelector('input[name="score"]');
    return input ? parseInt(input.value, 10) || 0 : 0;
  }

  function syncRatingGroup(group) {
    var stars = getStars(group);
    if (!stars.length) return;

    if (!group.getAttribute('role')) group.setAttribute('role', 'radiogroup');

    var score = getScore(group);
    // The selected star is the tab stop; before anything is chosen it is the first.
    var tabStop = score > 0 ? score - 1 : 0;

    stars.forEach(function (star, i) {
      if (!star.getAttribute('role')) star.setAttribute('role', 'radio');
      setIfChanged(star, 'aria-checked', String(i === score - 1));
      setIfChanged(star, 'tabindex', i === tabStop ? '0' : '-1');
    });
  }

  // Only write when the value actually differs. The observer below watches these
  // attributes, so an unconditional write would retrigger it forever.
  function setIfChanged(element, name, value) {
    if (element.getAttribute(name) === value) return;
    element.setAttribute(name, value);
  }

  /* =====================================================================
     Opening the form places focus past the rating

     jdgm.openForm ends with:
       e.find(".jdgm-form-wrapper input[type=text]:first()").focus()

     The first text input is Review Title, so opening the form focuses the SECOND
     field and skips the rating. The rating is the first control in the form and is
     aria-required, so a keyboard or screen reader user has to Shift+Tab backwards
     to find it - or never sees it and hits a validation error on submit.

     Put focus on the rating instead, once their handler has run.
     ===================================================================== */
  // Pick the rating of the form that is actually open. Judge.me renders more than
  // one form wrapper (review, question, and its dynamic variant), so taking the
  // first match can land on a hidden one - and then every check below is made
  // against the wrong form.
  function findOpenRatingGroup() {
    var groups = document.querySelectorAll('.jdgm-form__rating, .jdgm-form-dynamic__rating');

    for (var i = 0; i < groups.length; i++) {
      if (groups[i].offsetParent !== null && groups[i].querySelector('.jdgm-star')) {
        return groups[i];
      }
    }
    return null;
  }

  function isInRatingGroup(element) {
    return !!(element && element.closest &&
      element.closest('.jdgm-form__rating, .jdgm-form-dynamic__rating'));
  }

  function focusRatingTabStop() {
    // Two things have to be survived here, and a single attempt handles neither:
    //
    //   1. The form is built as it opens, so the rating is not in the DOM for the
    //      first few frames.
    //   2. Judge.me focuses Review Title AFTER we have focused the rating, so
    //      stopping at the first success just hands focus straight back - the
    //      rating visibly takes focus for a moment and then loses it.
    //
    // So keep re-claiming for a short window rather than trying once. Any real
    // interaction ends it immediately, so this can never fight the shopper.
    var deadline = performance.now() + 1000;
    var userActed = false;
    var landed = false;

    function noteUserAction() { userActed = true; }

    document.addEventListener('pointerdown', noteUserAction, true);
    document.addEventListener('keydown', noteUserAction, true);

    function stop() {
      document.removeEventListener('pointerdown', noteUserAction, true);
      document.removeEventListener('keydown', noteUserAction, true);

      if (!landed) {
        console.warn('[judgeme-a11y] Could not move focus to the rating when the form opened.',
          { activeElement: document.activeElement });
      }
    }

    function attempt() {
      if (userActed || performance.now() > deadline) return stop();

      var group = findOpenRatingGroup();

      if (group && !isInRatingGroup(document.activeElement)) {
        syncRatingGroup(group);
        var star = group.querySelector('.jdgm-star[tabindex="0"]') || getStars(group)[0];
        if (star) {
          star.focus();
          if (document.activeElement === star) landed = true;
        }
      }

      requestAnimationFrame(attempt);
    }

    requestAnimationFrame(attempt);
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (!e.target.closest('.jdgm-write-rev-link')) return;

    focusRatingTabStop();
  }, true);

  // Judge.me's shortcut handler covers only arrow keys (37/39) and 1-5 (49-53).
  // Space is what most people press to choose the focused radio, and Enter is a
  // common second guess - neither does anything today, and Space scrolls the page
  // instead. Adding them here does not collide with their key codes.
  document.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Spacebar' && e.key !== 'Enter') return;
    if (!e.target.closest) return;

    var star = e.target.closest('.jdgm-form__rating .jdgm-star');
    if (!star) return;

    e.preventDefault();

    // Raty listens for click, so this commits the focused star as the score.
    star.click();
  }, true);

  // Keep the tab stop and aria-checked correct after a mouse selection too. Raty
  // writes the score during its own handler, so read it back on the next tick.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var group = e.target.closest('.jdgm-form__rating');
    if (!group) return;
    window.setTimeout(function () { syncRatingGroup(group); }, 0);
  }, true);

  /* =====================================================================
     Fail loudly

     Everything above targets Judge.me's markup. The fixes hook their events rather
     than replacing their code, which survives their re-renders - but a class rename
     on their side would break every one of them with no visible symptom, and the
     next anyone would hear of it is the following accessibility audit.

     So warn once per missing hook. Nobody watches the console, but a broken fix that
     announces itself is findable in minutes; a silent one is not.
     ===================================================================== */
  var warned = {};

  function checkHook(selector, label, scope) {
    if (warned[selector]) return;
    if ((scope || document).querySelector(selector)) return;

    warned[selector] = true;
    console.warn(
      '[judgeme-a11y] Expected element not found: ' + selector + ' (' + label + '). ' +
      'A Judge.me markup change has probably broken this accessibility fix.'
    );
  }

  // The form is built on demand, so wait for the rating to appear before setting
  // up the roving tabindex. Raty can also re-render, which this picks up as well.
  //
  // Attributes are watched too, not just childList: Judge.me's score setter and
  // AudioEye's overlay both write tabindex and aria-checked on these same stars, and
  // whichever runs last wins. Without this the tab stop can be correct at render and
  // quietly reset to -1 a moment later - which looks identical to it never being set.
  //
  // The same pass checks the hooks each fix depends on, as they become available.
  var checkedWidget = false;

  new MutationObserver(function () {
    document.querySelectorAll('.jdgm-form__rating').forEach(syncRatingGroup);

    // The widget loads asynchronously, so only check once it has actually rendered.
    if (!checkedWidget && document.querySelector('.jdgm-rev-widg')) {
      checkedWidget = true;
      checkHook('.jdgm-prev-badge', 'review badge - finding 3');
      checkHook('.jdgm-rev-widg__title', 'reviews heading, the focus target for finding 3');
    }

    // Both forms are built on open, so check their internals only once one exists.
    var reviewForm = document.querySelector('.jdgm-form-wrapper form');
    if (reviewForm) checkHook('.jdgm-form__rating', 'star rating - finding 4', reviewForm);

    var questionForm = document.querySelector('.jdgm-question-form-wrapper form');
    if (questionForm) checkHook('.jdgm-cancel-question', 'question form cancel - finding 2', questionForm);
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['tabindex', 'aria-checked']
  });

  document.querySelectorAll('.jdgm-form__rating').forEach(syncRatingGroup);

  // If the widget never renders at all, none of the above can run. Ten seconds is
  // well past a slow third-party load, so silence by then means something is wrong.
  window.setTimeout(function () {
    checkHook('.jdgm-rev-widg', 'Judge.me review widget - none of these fixes are active');
  }, 10000);
})();
